import { useCallback } from 'react';
import { getValidIdToken } from './cognito';
import { getIdentityPoolCredentials } from './awsCredentials';
import { signedGet, signedPost, signedDelete, signedPut } from './api';

export function useApi() {
  const getCredentials = useCallback(async () => {
    // Always resolve a fresh id token (refreshing if needed) rather than reading a possibly-stale
    // one off React state, so long-lived sessions keep working after the id token rotates.
    const idToken = await getValidIdToken();
    if (!idToken) throw new Error('No active session');
    return getIdentityPoolCredentials(idToken);
  }, []);

  const apiGet = useCallback(async <T>(path: string): Promise<T> => {
    const credentials = await getCredentials();
    return signedGet<T>(path, credentials);
  }, [getCredentials]);

  const apiPost = useCallback(async <T>(path: string, body: unknown): Promise<T> => {
    const credentials = await getCredentials();
    return signedPost<T>(path, body, credentials);
  }, [getCredentials]);

  const apiPut = useCallback(async <T>(path: string, body: unknown): Promise<T> => {
    const credentials = await getCredentials();
    return signedPut<T>(path, body, credentials);
  }, [getCredentials]);

  const apiDelete = useCallback(async <T>(path: string): Promise<T> => {
    const credentials = await getCredentials();
    return signedDelete<T>(path, credentials);
  }, [getCredentials]);

  return { apiGet, apiPost, apiPut, apiDelete };
}
